import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  ViewChild,
} from '@angular/core'
import {FormBuilder, FormGroup} from '@angular/forms'
import {Subject} from 'rxjs'
import moment from 'moment'
import {
  DateFilter,
  DateFilterEnum,
} from '@app/core/services/date-filter/date-filter.interface'
import {NgxDaterangepickerBootstrapDirective} from 'ngx-daterangepicker-bootstrap'
import {ContextService} from '@app/core/services/context.service'
import {LocalStorageService} from '@app/core/services/local-storage.service'

@Component({
  selector: 'date-filter',
  templateUrl: './date-filter.component.html',
  styleUrls: ['./date-filter.component.scss'],
  standalone: false,
})
export class DateFilterComponent implements OnInit, OnDestroy {
  @Input() type: DateFilterEnum
  @Input() showTime = true
  @Output() rangeChange: EventEmitter<DateFilter> =
    new EventEmitter<DateFilter>(true)

  @ViewChild(NgxDaterangepickerBootstrapDirective, {static: false})
  pickerDirective: NgxDaterangepickerBootstrapDirective

  form: FormGroup
  periodSelect = false
  selected: {startDate: moment.Moment; endDate: moment.Moment}

  dateEnums = DateFilterEnum
  dateOptions = [
    {value: DateFilterEnum.CUR_DAY.valueOf(), label: 'Today'},
    {value: DateFilterEnum.PREV_DAY.valueOf(), label: 'Yesterday'},
    {value: DateFilterEnum.PREV_7DAYS.valueOf(), label: 'Last 7 days'},
    {value: DateFilterEnum.CUR_WEEK.valueOf(), label: 'Current week'},
    {value: DateFilterEnum.PREV_WEEK.valueOf(), label: 'Previous week'},
    {value: DateFilterEnum.CUR_MONTH.valueOf(), label: 'Current month'},
    {value: DateFilterEnum.PREV_MONTH.valueOf(), label: 'Previous month'},
    {value: DateFilterEnum.CUR_YEAR.valueOf(), label: 'Current year'},
    {value: DateFilterEnum.PREV_YEAR.valueOf(), label: 'Previous year'},
  ]

  // Configuration for ngx-daterangepicker-bootstrap
  locale = {
    format: 'DD-MM-YY',
    displayFormat: 'DD-MM-YY',
    separator: ' - ',
    applyLabel: 'Apply',
    cancelLabel: 'Cancel',
    fromLabel: 'From',
    toLabel: 'To',
    customRangeLabel: 'Custom',
    weekLabel: 'W',
    firstDay: 1,
  }

  // Ranges for the date picker (converted from dateOptions)
  ranges: any = {}
  acknowledgedNewFeature = false

  private end$ = new Subject<void>()

  constructor(
    private fb: FormBuilder,
    private ctxService: ContextService,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit() {
    this.localStorageService.getItem(
      this.ctxService.userId + '_daterange_acknowledged',
    ) === 'true'
      ? (this.acknowledgedNewFeature = true)
      : (this.acknowledgedNewFeature = false)
    this.periodSelect = true

    // Build ranges object from dateOptions
    this.buildRanges()

    // Initialize selected range
    this.selected = this.getDateRangeFromPeriod(this.type)

    // Initialize form
    this.form = this.fb.group({
      period: this.fb.control(this.type),
    })

    // Set initial range
    this.setRange(this.type)
    this.periodSelect = false
  }

  private buildRanges() {
    this.dateOptions.forEach((option) => {
      const range = this.getDateRangeFromPeriod(option.value)
      this.ranges[option.label] = [range.startDate, range.endDate]
    })
  }

  ngOnDestroy() {
    this.end$.next()
    this.end$.complete()
  }

  private getDateRangeFromPeriod(t: DateFilterEnum): {
    startDate: moment.Moment
    endDate: moment.Moment
  } {
    let start: moment.Moment
    let end: moment.Moment

    switch (t) {
      case DateFilterEnum.CUR_DAY:
        start = moment().startOf('day').hour(0).minute(0).second(0)
        end = moment().endOf('day').hour(23).minute(59).second(59)
        break
      case DateFilterEnum.PREV_DAY:
        start = moment()
          .subtract(1, 'day')
          .startOf('day')
          .hour(0)
          .minute(0)
          .second(0)
        end = moment()
          .subtract(1, 'day')
          .endOf('day')
          .hour(23)
          .minute(59)
          .second(59)
        break
      case DateFilterEnum.PREV_7DAYS:
        start = moment()
          .subtract(6, 'day')
          .startOf('day')
          .hour(0)
          .minute(0)
          .second(0)
        end = moment().endOf('day').hour(23).minute(59).second(59)
        break
      case DateFilterEnum.CUR_WEEK:
        start = moment().startOf('isoWeek').hour(0).minute(0).second(0)
        end = moment().endOf('isoWeek').hour(23).minute(59).second(59)
        break
      case DateFilterEnum.PREV_WEEK:
        start = moment()
          .subtract(1, 'week')
          .startOf('isoWeek')
          .hour(0)
          .minute(0)
          .second(0)
        end = moment()
          .subtract(1, 'week')
          .endOf('isoWeek')
          .hour(23)
          .minute(59)
          .second(59)
        break
      case DateFilterEnum.CUR_MONTH:
        start = moment().startOf('month').hour(0).minute(0).second(0)
        end = moment().endOf('month').hour(23).minute(59).second(59)
        break
      case DateFilterEnum.PREV_MONTH:
        start = moment()
          .subtract(1, 'month')
          .startOf('month')
          .hour(0)
          .minute(0)
          .second(0)
        end = moment()
          .subtract(1, 'month')
          .endOf('month')
          .hour(23)
          .minute(59)
          .second(59)
        break
      case DateFilterEnum.CUR_YEAR:
        start = moment().startOf('year').hour(0).minute(0).second(0)
        end = moment().endOf('year').hour(23).minute(59).second(59)
        break
      case DateFilterEnum.PREV_YEAR:
        start = moment()
          .subtract(1, 'year')
          .startOf('year')
          .hour(0)
          .minute(0)
          .second(0)
        end = moment()
          .subtract(1, 'year')
          .endOf('year')
          .hour(23)
          .minute(59)
          .second(59)
        break
      default:
        start = moment()
          .subtract(7, 'day')
          .startOf('day')
          .hour(0)
          .minute(0)
          .second(0)
        end = moment().endOf('day').hour(23).minute(59).second(59)
        break
    }

    return {startDate: start, endDate: end}
  }

  setRange(t: DateFilterEnum) {
    this.selected = this.getDateRangeFromPeriod(t)
    this.emitRangeChange()
  }

  private emitRangeChange() {
    if (this.selected && this.selected.startDate && this.selected.endDate) {
      const dateFilter: DateFilter = {
        start: this.selected.startDate.valueOf(),
        end: this.selected.endDate.valueOf(),
      }
      this.rangeChange.emit(dateFilter)
    }
  }

  onChange() {
    this.periodSelect = true
    const periodValue = this.form.get('period').value
    if (periodValue !== null && periodValue !== undefined) {
      this.setRange(periodValue)
    }
    this.periodSelect = false
  }

  // Event handler for date range picker
  choosedDate(value: any, datepicker?: any) {
    // Update selected range
    this.selected = value

    // Update period dropdown based on selected range label
    if (value.label) {
      const matchingOption = this.dateOptions.find(
        (opt) => opt.label === value.label,
      )
      if (matchingOption) {
        this.periodSelect = true
        this.form
          .get('period')
          .setValue(matchingOption.value, {emitEvent: false})
        this.periodSelect = false
      } else {
        // Custom range selected
        this.form.get('period').setValue(null, {emitEvent: false})
      }
    } else if (!this.periodSelect) {
      // Clear period selection when custom range is chosen manually
      this.form.get('period').setValue(null, {emitEvent: false})
    }

    // Emit the change
    this.emitRangeChange()
  }

  // Handler for when range is clicked from the ranges list
  rangeClicked(range: any) {
    const matchingOption = this.dateOptions.find(
      (opt) => opt.label === range.label,
    )
    if (matchingOption) {
      this.periodSelect = true
      this.form.get('period').setValue(matchingOption.value, {emitEvent: false})
      this.periodSelect = false
    }
  }

  // Handler for manual input (Enter key)
  onInputKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault()
      const input = event.target as HTMLInputElement
      const value = input.value

      // Parse the manually entered date range
      if (value && value.includes(this.locale.separator)) {
        const dates = value.split(this.locale.separator).map((d) => d.trim())
        if (dates.length === 2) {
          const startDate = moment(dates[0], this.locale.format)
          const endDate = moment(dates[1], this.locale.format)

          if (startDate.isValid() && endDate.isValid()) {
            // Apply time defaults
            if (this.showTime) {
              startDate.hour(0).minute(0).second(0)
              endDate.hour(23).minute(59).second(59)
            }

            this.selected = {
              startDate: startDate,
              endDate: endDate,
            }

            // Clear period selection for manual input
            this.form.get('period').setValue(null, {emitEvent: false})

            // Close the picker if open
            if (this.pickerDirective) {
              this.pickerDirective.hide()
            }

            // Emit the change
            this.emitRangeChange()
          }
        }
      }
    }
  }

  openDatePicker(event: Event) {
    event.stopPropagation() // Prevent event bubbling to parent elements
    if (this.pickerDirective) {
      this.pickerDirective.open() // Programmatically open the date picker
    }
  }

  acknowledgeFeature() {
    this.acknowledgedNewFeature = true
    this.localStorageService.setItem(
      this.ctxService.userId + '_daterange_acknowledged',
      'true',
    )
  }
}
